import React, { useEffect, useState } from 'react';
import { ChevronLeft, CheckCircle2, ShieldCheck, Pencil } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
interface ReviewSubmitJourneyProps {
  businessInfo?: any;
  personalInfo?: any;
  documents?: any;
  bankAccount?: any;
  accountSecurity?: {
    method?: string;
  };
  /** Section id matching SectionsOverview ids: business-info, personal-info, documents, bank-account, account-security */
  onEditSection: (sectionId: string) => void;
  onExit: () => void;
  onSubmit: () => void;
}
const SECURITY_METHOD_LABELS: Record<string, string> = {
  secure2u: 'Maybank Secure2u Configured',
  authenticator: 'Authenticator App Configured',
  hardware_token: 'Hardware Security Token Configured'
};
const BUSINESS_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  sole_proprietor: 'Sole Proprietorship',
  partnership: 'Partnership',
  corporation: 'Corporation / Sdn Bhd',
  non_profit: 'Non-profit'
};
const ID_METHOD_LABELS: Record<string, string> = {
  national_id: 'National ID',
  passport: 'Passport'
};
const maskAccountNumber = (raw: string): string => {
  if (!raw) return '—';
  const last4 = raw.slice(-4);
  return `•••• •••• ${last4}`;
};
const formatAddress = (address: any): string => {
  if (!address || !address.line1) return '—';
  const parts = [address.line1, address.line2, `${address.postalCode} ${address.city}`, address.state].filter(Boolean);
  return parts.join(', ');
};
export function ReviewSubmitJourney({
  businessInfo,
  personalInfo,
  documents,
  bankAccount,
  accountSecurity,
  onEditSection,
  onExit,
  onSubmit
}: ReviewSubmitJourneyProps) {
  const [agreed, setAgreed] = useState(false);
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-272">
      {/* Back link */}
      <button type="button" onClick={onExit} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6 self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-273">
        <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-274" />
        Back to overview
      </button>

      {/* Header */}
      <header className="mb-8" data-id="element-275">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-276">
          Review and submit
        </h1>
        <p className="text-base text-gray-600 leading-relaxed" data-id="element-277">
          Please review your details. You won't be able to submit until all
          required sections are complete and accurate.
        </p>
      </header>

      {/* Summary cards */}
      <div className="space-y-4" data-id="element-278">
        {/* Card 1 — Business Details */}
        <SummaryCard title="Business Details" onEdit={() => onEditSection('business-info')} data-id="element-279">
          <KeyValue label="Business Type" value={BUSINESS_TYPE_LABELS[businessInfo?.businessType] || '—'} data-id="element-280" />
          <KeyValue label="Legal Business Name" value={businessInfo?.legalName || '—'} data-id="element-281" />
          {businessInfo?.dba && <KeyValue label="Doing Business As" value={businessInfo.dba} data-id="element-282" />}
          <KeyValue label="Business Registration Number" value={businessInfo?.regNumber || '—'} data-id="element-283" />
          <KeyValue label="Registered Address" value={formatAddress(businessInfo?.address)} data-id="element-284" />
          <div className="pt-3 mt-3 border-t border-gray-100 space-y-3" data-id="element-285">
            <KeyValue label="Product Category" value={businessInfo?.productCategory || '—'} valueClassName="capitalize" data-id="element-286" />
            <KeyValue label="Description" value={businessInfo?.description || '—'} data-id="element-287" />
          </div>
          {(businessInfo?.tin || businessInfo?.sstRegistered) && <div className="pt-3 mt-3 border-t border-gray-100 space-y-3" data-id="element-288">
              {businessInfo?.tin && <KeyValue label="Tax ID (TIN)" value={businessInfo.tin} data-id="element-289" />}
              {businessInfo?.sstRegistered && <KeyValue label="SST Registered" value={businessInfo.sstRegistered === 'yes' ? 'Yes' : 'No'} data-id="element-290" />}
              {businessInfo?.sstId && <KeyValue label="SST ID" value={businessInfo.sstId} data-id="element-291" />}
            </div>}
          <div className="pt-3 mt-3 border-t border-gray-100 space-y-3" data-id="element-292">
            <KeyValue label="Statement Descriptor" value={businessInfo?.statementDescriptor || '—'} data-id="element-293" />
            {businessInfo?.shortDescriptor && <KeyValue label="Short Descriptor" value={businessInfo.shortDescriptor} data-id="element-294" />}
            <KeyValue label="Support Phone" value={businessInfo?.supportPhone ? `+60 ${businessInfo.supportPhone}` : '—'} data-id="element-295" />
          </div>
        </SummaryCard>

        {/* Card 2 — Representative Details */}
        <SummaryCard title="Representative Details" onEdit={() => onEditSection('personal-info')} data-id="element-296">
          <KeyValue label="Name" value={personalInfo?.fullName || '—'} data-id="element-297" />
          <KeyValue label="Email" value={personalInfo?.email || '—'} data-id="element-298" />
          <KeyValue label="Date of Birth" value={personalInfo?.dob || '—'} data-id="element-299" />
          <KeyValue label="Phone" value={personalInfo?.phone ? `+60 ${personalInfo.phone}` : '—'} data-id="element-300" />

          <div className="pt-3 mt-3 border-t border-gray-100 space-y-3" data-id="element-301">
            <KeyValue label="ID Type" value={ID_METHOD_LABELS[personalInfo?.idMethod] || '—'} data-id="element-302" />
            <KeyValue label="ID Number" value={personalInfo?.idNumber || '—'} data-id="element-303" />
            {personalInfo?.idMethod === 'passport' && personalInfo?.passportCountry && <KeyValue label="Passport Country" value={personalInfo.passportCountry} data-id="element-304" />}
          </div>

          <div className="pt-3 mt-3 border-t border-gray-100 space-y-3" data-id="element-305">
            <KeyValue label="Home Address" value={formatAddress(personalInfo?.address)} data-id="element-306" />
          </div>
        </SummaryCard>

        {/* Card 3 — Supporting Documents */}
        <SummaryCard title="Supporting Documents" onEdit={() => onEditSection('documents')} data-id="element-307">
          {documents?.idFront && <DocRow label="Identity Document (Front)" file={documents.idFront} data-id="element-308" />}
          {documents?.idBack && <DocRow label="Identity Document (Back)" file={documents.idBack} data-id="element-309" />}
          {documents?.bizDoc1 && <DocRow label="Business Document 1" file={documents.bizDoc1} data-id="element-310" />}
          {documents?.bizDoc2 && <DocRow label="Business Document 2" file={documents.bizDoc2} data-id="element-311" />}
          {!documents?.idFront && !documents?.bizDoc1 && <p className="text-sm text-gray-600" data-id="element-312">No documents uploaded.</p>}
        </SummaryCard>

        {/* Card 4 — Payout Details */}
        <SummaryCard title="Payout Details" onEdit={() => onEditSection('bank-account')} data-id="element-313">
          <KeyValue label="Bank" value={bankAccount?.bank || '—'} data-id="element-314" />
          <KeyValue label="Account Number" value={maskAccountNumber(bankAccount?.accountNumber || '')} valueClassName="font-mono tracking-wider" data-id="element-315" />
          {bankAccount?.bankStatement && <div className="mt-2 pt-3 border-t border-gray-100" data-id="element-316">
              <DocRow label="Bank Statement" file={bankAccount.bankStatement} data-id="element-317" />
            </div>}
        </SummaryCard>

        {/* Card 5 — Account Security */}
        <SummaryCard title="Account Security" onEdit={() => onEditSection('account-security')} data-id="element-318">
          <div className="flex items-start justify-between gap-3" data-id="element-319">
            <div data-id="element-320">
              <p className="text-xs text-gray-600 mb-0.5" data-id="element-321">
                Two-step authentication
              </p>
              <p className="text-sm font-medium text-gray-900" data-id="element-322">
                {accountSecurity?.method ? SECURITY_METHOD_LABELS[accountSecurity.method] || 'Authentication Configured' : 'Not configured'}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-800 text-xs font-semibold" data-id="element-323">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" data-id="element-324" />
              Active
            </span>
          </div>
        </SummaryCard>
      </div>

      {/* Consent + Submit (sticky) */}
      <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-10" data-id="element-325">
        <div className="py-5 px-2 space-y-4" data-id="element-326">
          <Checkbox name="merchantConsent" checked={agreed} onChange={e => setAgreed(e.target.checked)} label={<>
                I agree to the{' '}
                <a href="#" onClick={e => e.preventDefault()} className="font-semibold text-gray-900 underline underline-offset-2 hover:text-[#B8860B]" data-id="element-328">
                  Maybank Merchant Services Agreement
                </a>{' '}
                and confirm that the information provided is accurate and
                authorized.
              </>} data-id="element-327" />
          <Button fullWidth onClick={onSubmit} disabled={!agreed} data-id="element-329">
            Agree and Submit
          </Button>
        </div>
      </div>
    </div>;
}
// ---------- Sub-components ----------
function SummaryCard({
  title,
  onEdit,
  children
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return <section aria-label={title} className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden" data-id="element-330">
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100" data-id="element-331">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider" data-id="element-332">
          {title}
        </h2>
        <button type="button" onClick={onEdit} aria-label={`Edit ${title}`} className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 underline underline-offset-2 hover:text-[#B8860B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8860B] focus-visible:ring-offset-2 rounded" data-id="element-333">
          <Pencil className="w-3.5 h-3.5" aria-hidden="true" data-id="element-334" />
          Edit
        </button>
      </header>
      <div className="px-5 py-4 space-y-3" data-id="element-335">{children}</div>
    </section>;
}
function KeyValue({
  label,
  value,
  valueClassName = ''
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return <div className="grid grid-cols-[120px_minmax(0,1fr)] sm:grid-cols-[140px_minmax(0,1fr)] gap-3 items-start" data-id="element-336">
      <dt className="text-xs text-gray-600 mt-0.5" data-id="element-337">{label}</dt>
      <dd className={`text-sm font-medium text-gray-900 min-w-0 [overflow-wrap:anywhere] ${valueClassName}`} data-id="element-338">
        {value}
      </dd>
    </div>;
}
function DocRow({
  label,
  file
}: {
  label: string;
  file: File;
}) {
  const [url, setUrl] = useState<string | null>(null);
  // Create object URL for the file so it can be opened
  useEffect(() => {
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [file]);
  return <div className="grid grid-cols-[120px_minmax(0,1fr)] sm:grid-cols-[140px_minmax(0,1fr)] gap-3 items-start" data-id="element-339">
      <dt className="text-xs text-gray-600 mt-0.5" data-id="element-340">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 min-w-0 [overflow-wrap:anywhere] flex items-center gap-1.5" data-id="element-341">
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" aria-label="Uploaded" data-id="element-342" />
        {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="break-all hover:text-blue-600 hover:underline transition-colors" data-id="element-343">
            {file.name}
          </a> : <span className="break-all" data-id="element-344">{file.name}</span>}
      </dd>
    </div>;
}